<template>
  <div class="flex w-full items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
    
    <!-- LEFT SIDE: Toggle (Mobile Only) + Page Title -->
    <div class="flex items-center gap-x-4">
      <button 
        @click="$emit('toggle-sidebar')"
        class="md:hidden p-2 -ml-2 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>
      
      <h1 class="text-lg font-semibold text-gray-900 dark:text-white truncate">
        {{ pageTitle }}
      </h1>
    </div>
    
    <!-- RIGHT SIDE: Actions -->
    <div class="flex items-center gap-x-2 sm:gap-x-4">
      
      <!-- Language Selector -->
      <div class="relative hidden sm:block">
        <select @change="$emit('change-lang', $event)" class="appearance-none w-28 cursor-pointer rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 py-1.5 pl-3 pr-8 text-sm font-medium text-gray-700 dark:text-gray-200 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#035a76]">
          <option value="en">🇬🇧 English</option>
          <option value="fr">🇫🇷 Français</option>
        </select>
        <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
          <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" /></svg>
        </div>
      </div>

      <!-- Dark Mode Toggle -->
      <button @click="$emit('toggle-dark-mode')" class="p-2 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
        <svg v-if="!isDarkMode" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg>
        <svg v-else class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
      </button>
      
      <!-- ✅ UPDATED: Notifications Dropdown with Read/Unread Tracking -->
      <div ref="notificationDropdownRef" class="relative">
        <button 
          @click="isNotificationOpen = !isNotificationOpen" 
          class="relative p-2 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
          <!-- ✅ Dynamic Badge: Only shows unread count -->
          <span v-if="unreadCount > 0" class="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white ring-2 ring-white dark:ring-gray-800 animate-pulse">
            {{ unreadCount > 9 ? '9+' : unreadCount }}
          </span>
        </button>
        
        <!-- Dropdown Menu -->
        <div v-if="isNotificationOpen" class="absolute right-0 z-50 mt-2 w-80 origin-top-right rounded-xl bg-white dark:bg-gray-800 py-2 shadow-lg ring-1 ring-gray-900/5 dark:ring-gray-700">
          <div class="px-4 py-2 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center">
            <h3 class="text-sm font-semibold text-gray-900 dark:text-white">Notifications</h3>
            <div class="flex items-center gap-2">
              <span v-if="unreadCount > 0" class="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 rounded-full">
                {{ unreadCount }} New
              </span>
              <button 
                v-if="unreadCount > 0" 
                @click="markAllAsRead"
                class="text-xs text-[#035a76] dark:text-blue-400 hover:underline font-medium"
              >
                Mark all read
              </button>
            </div>
          </div>
          
          <div class="max-h-96 overflow-y-auto">
            <div v-if="pendingLeaves.length === 0" class="px-4 py-8 text-center">
              <svg class="mx-auto h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">No pending leave requests</p>
            </div>
            
            <div v-else>
              <a 
                v-for="leave in pendingLeaves" 
                :key="leave.id"
                @click.prevent="viewLeaveRequest(leave)"
                href="#"
                class="block px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors border-b border-gray-50 dark:border-gray-700 last:border-0 relative"
                :class="{ 'bg-blue-50/50 dark:bg-blue-900/10': !isRead(leave.id) }"
              >
                <!-- Unread indicator dot -->
                <span v-if="!isRead(leave.id)" class="absolute top-4 left-2 h-2 w-2 rounded-full bg-blue-500"></span>
                
                <div class="flex items-start gap-3">
                  <div class="flex-shrink-0">
                    <div class="h-8 w-8 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center text-amber-600 dark:text-amber-400">
                      <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-medium text-gray-900 dark:text-white truncate">
                      New Leave Request
                    </p>
                    <p class="text-xs text-gray-600 dark:text-gray-300 mt-0.5">
                      <span class="font-semibold">{{ leave.employeeName }}</span> requested {{ leave.type }} leave.
                    </p>
                    <p class="text-xs text-gray-400 dark:text-gray-500 mt-1">
                      {{ formatDate(leave.submittedOn) }}
                    </p>
                  </div>
                </div>
                <div class="mt-2 flex justify-end">
                  <span class="text-xs font-medium text-[#035a76] dark:text-blue-400 hover:underline">View Details →</span>
                </div>
              </a>
            </div>
          </div>
          
          <div class="px-4 py-2 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-b-xl">
            <button @click.prevent="viewAllRequests" class="w-full text-center text-xs font-medium text-[#035a76] dark:text-blue-400 hover:underline">
              View all pending requests
            </button>
          </div>
        </div>
      </div>
      
      <!-- Divider -->
      <div class="hidden sm:block h-6 w-px bg-gray-200 dark:bg-gray-700"></div>
      
      <!-- User Dropdown -->
      <div ref="userDropdownRef" class="relative">
        <button @click="isUserDropdownOpen = !isUserDropdownOpen" class="flex items-center gap-x-3 rounded-lg p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
          <div class="h-8 w-8 rounded-full bg-[#035a76] flex items-center justify-center text-sm font-semibold text-white shadow-sm">
            {{ getInitials(user?.name) }}
          </div>
          <div class="hidden md:flex flex-col items-start">
            <span class="text-sm font-semibold text-gray-900 dark:text-white leading-tight">{{ user?.name || "Guest" }}</span>
            <span class="text-xs text-gray-500 dark:text-gray-400">Administrator</span>
          </div>
          <svg class="hidden md:block h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" /></svg>
        </button>
        
        <div v-if="isUserDropdownOpen" class="absolute right-0 z-50 mt-2 w-48 origin-top-right rounded-xl bg-white dark:bg-gray-800 py-2 shadow-lg ring-1 ring-gray-900/5 dark:ring-gray-700">
          <a href="#" class="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">Your Profile</a>
          <a href="#" class="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">Settings</a>
          <div class="my-1 border-t border-gray-100 dark:border-gray-700"></div>
          <button @click.prevent="logout" class="flex w-full items-center px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20">
            Sign out
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAuth } from '@/composables/useAuth';
import api from '@/services/api';

const { locale } = useI18n();
const { logout } = useAuth();
const router = useRouter();
const route = useRoute();

const props = defineProps({
  pageTitle: { type: String, default: 'Dashboard' },
  isDarkMode: { type: Boolean, default: false }
});

const emit = defineEmits(['toggle-sidebar', 'toggle-dark-mode', 'change-lang', 'logout', 'view-leave-request']);

const user = ref(null);
const isUserDropdownOpen = ref(false);
const userDropdownRef = ref(null);

// ✅ Notification State with Read Tracking
const isNotificationOpen = ref(false);
const notificationDropdownRef = ref(null);
const pendingLeaves = ref([]);
const viewedNotificationIds = ref([]);

const UNREAD_STORAGE_KEY = 'hr_viewed_notifications';

// Load previously viewed notification IDs from localStorage
const loadViewedIds = () => {
  try {
    const stored = localStorage.getItem(UNREAD_STORAGE_KEY);
    viewedNotificationIds.value = stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error loading viewed notifications:', error);
    viewedNotificationIds.value = [];
  }
};

const saveViewedIds = () => {
  try {
    localStorage.setItem(UNREAD_STORAGE_KEY, JSON.stringify(viewedNotificationIds.value));
  } catch (error) {
    console.error('Error saving viewed notifications:', error);
  }
};

// ✅ Computed: Only count unread notifications
const unreadCount = computed(() => {
  return pendingLeaves.value.filter(l => !viewedNotificationIds.value.includes(String(l.id))).length;
});

const isRead = (id) => {
  return viewedNotificationIds.value.includes(String(id));
};

const markAllAsRead = () => {
  const allIds = pendingLeaves.value.map(l => String(l.id));
  // Merge with existing viewed IDs (avoid duplicates)
  const merged = [...new Set([...viewedNotificationIds.value, ...allIds])];
  viewedNotificationIds.value = merged;
  saveViewedIds();
};

const fetchPendingLeaves = async () => {
  try {
    const response = await api.get('Leaves.php');
    const leaves = response.data?.data || [];
    
    const newPendingLeaves = leaves
      .filter(l => l.status === 'pending')
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 10)
      .map(l => ({
        id: String(l.id),
        applicant_id: String(l.applicant_id),
        employeeName: l.employee_name || 'Unknown Employee',
        type: l.leave_type_name || 'Leave',
        submittedOn: l.created_at
      }));
    
    pendingLeaves.value = newPendingLeaves;
    
    // ✅ Clean up viewed IDs that are no longer pending (approved/rejected)
    const currentPendingIds = newPendingLeaves.map(l => l.id);
    viewedNotificationIds.value = viewedNotificationIds.value.filter(id => currentPendingIds.includes(id));
    saveViewedIds();
    
  } catch (error) {
    console.error('Error fetching pending leaves:', error);
  }
};

const viewLeaveRequest = (leave) => {
  // ✅ Mark this notification as read
  if (!viewedNotificationIds.value.includes(leave.id)) {
    viewedNotificationIds.value.push(leave.id);
    saveViewedIds();
  }
  
  isNotificationOpen.value = false;
  
  // ✅ Navigate to HR Leave Management page with the leave ID in query
  router.push({
    path: '/leave',
    query: { viewLeave: leave.id }
  });
};

const viewAllRequests = () => {
  isNotificationOpen.value = false;
  router.push({ path: '/leave' });
};

const handleClickOutside = (event) => {
  if (userDropdownRef.value && !userDropdownRef.value.contains(event.target)) {
    isUserDropdownOpen.value = false;
  }
  if (notificationDropdownRef.value && !notificationDropdownRef.value.contains(event.target)) {
    isNotificationOpen.value = false;
  }
};

// ✅ Auto-refresh notifications every 30 seconds
let refreshInterval = null;

onMounted(() => {
  const storedUser = localStorage.getItem("user");
  if (storedUser) user.value = JSON.parse(storedUser);
  
  document.addEventListener('click', handleClickOutside);
  loadViewedIds();
  fetchPendingLeaves();
  
  // Poll for new notifications every 30 seconds
  refreshInterval = setInterval(fetchPendingLeaves, 30000);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  if (refreshInterval) clearInterval(refreshInterval);
});

const getInitials = (name) => {
  if (!name) return "G";
  const parts = name.trim().split(" ");
  return parts.length === 1 ? parts[0].charAt(0).toUpperCase() : (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};
</script>